/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Divider, Form, Row } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { useLocation, useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import moment from 'moment'
import queryString from 'query-string'
import { formElementPropsGenerator } from '../../lib/forms'
import { DatePicker, Input, SubmitButton } from '../Forms'

import api from '../../api'
import { toPath } from '../../routes'
import DeviceModelSelect from '../Forms/DeviceModelSelect'
import PracticeSelect from '../Forms/PracticeSelect'
import AsyncSelect from '../Forms/AsyncSelect'

const TRANSMISSION_DEFAULTS = {
  patientId: undefined,
  practiceId: undefined,
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  deviceModelId: '',
  deviceSerialNumber: '',
}

const TRANSMISSION_LABELS = {
  patientId: 'Search for patient',
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  practiceId: 'Practice',
  selectedPracticeId: 'Practice',
  deviceModelId: 'Model #',
  deviceSerialNumber: 'Serial Number',
  cptCode: 'CPT Code',
  icd10Code: 'ICD-10 Code',
  authorized: 'Authorized',
  practiceMrn: 'Practice MRN',
}

const propsFor = formElementPropsGenerator({
  formName: 'transmission-form',
  labels: TRANSMISSION_LABELS,
  placeholders: {
    deviceModelId: 'Select a new device model',
  },
  practice: { key: undefined, value: undefined },
})

const labelCol = {
  sm: { span: 6, offset: 0 },
}

const wrapperCol = {
  sm: { span: 15 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: 10 },
}

const patientCreationSchema = () => {
  const shape = {
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
  }

  return Yup.object().shape(shape)
}

const buildValidationSchema = () => {
  const shape = {
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    dateOfBirth: Yup.date()
      .max(new Date())
      .required(),
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
    useCurrentDevice: Yup.boolean().required(),
  }

  return Yup.object().shape(shape)
}

const PatientSelector = ({ showInactive, practiceId, ...rest }) => {
  const fetchOptions = useCallback(
    q =>
      api.patients.getPatients({ q, showInactive, practiceId }).then(res =>
        res.body.map(patient => {
          const formatedBirthDate = moment(patient.birthDate).format(
            'MM-DD-YYYY',
          )
          const fullName = `${patient.firstName} ${patient.lastName}`
          let title = `${fullName} - ${formatedBirthDate}`
          if (!patient.active) {
            title = `${title} (Inactive)`
          }

          return { id: patient.id, name: title }
        }),
      ),
    [showInactive, practiceId],
  )

  return (
    <AsyncSelect
      asyncSearch
      fetchOptions={fetchOptions}
      initialFetch={false}
      showSearch
      optionFilterProp="children"
      placeholder="Start typing to find a patient"
      {...rest}
    />
  )
}

PatientSelector.propTypes = {
  showInactive: PropTypes.bool,
  practiceId: PropTypes.string,
}

PatientSelector.defaultProps = {
  showInactive: false,
  practiceId: undefined,
}

const CustomPatientSelector = ({
  paramsPatientId,
  updatePractice,
  deviceSerialNumber,
  patientCreationTogler,
  setMatchedDevice,
  practice,
}) => {
  const { values, setValues, setFieldValue } = useFormikContext()

  const { patientId } = values
  const id = paramsPatientId || patientId
  const [selectedPracticeId, setSelectedPracticeId] = useState()

  useMemo(() => {
    if (practice) {
      setValues({ ...TRANSMISSION_DEFAULTS, practice })
      setSelectedPracticeId(practice.key)
    }
  }, [practice, setValues, setSelectedPracticeId])

  useEffect(() => {
    if (id) {
      api.patients.get(id).then(response => {
        const useCurrentDevice =
          deviceSerialNumber === response.currentDevice?.serialNumber
        setValues({
          ...TRANSMISSION_DEFAULTS,
          patientId: response.id,
          firstName: response.firstName,
          lastName: response.lastName,
          dateOfBirth: moment(response.birthDate),
          practiceMrn: response.practiceMrn,
          deviceModelId: useCurrentDevice
            ? response.currentDevice.deviceModelId
            : undefined,
          deviceSerialNumber:
            deviceSerialNumber || response.currentDevice?.serialNumber,
          useCurrentDevice,
        })

        if (useCurrentDevice) setMatchedDevice(true)

        setFieldValue('practice', {
          key: response.practice.id,
          value: response.practice.name,
        })
        patientCreationTogler(false)
      })
    } else {
      setValues({ ...TRANSMISSION_DEFAULTS, deviceSerialNumber })
      patientCreationTogler(true)
    }
  }, [
    id,
    setValues,
    setFieldValue,
    deviceSerialNumber,
    updatePractice,
    patientCreationTogler,
    setMatchedDevice,
  ])
  if (paramsPatientId) {
    return <></> // hide patient selector if patientId is in the URL
  }

  return (
    <>
      <PracticeSelect
        {...propsFor('practice')}
        labelAlign="right"
        labelInValue
        allowClear={false}
        onChange={value => {
          setSelectedPracticeId(value.key)
          setFieldValue('practice', value)
        }}
      />
      <PatientSelector
        {...propsFor('patientId')}
        placeholder="Search by name, device model or serial number"
        allowClear
        showInactive
        practiceId={selectedPracticeId}
      />
    </>
  )
}

CustomPatientSelector.propTypes = {
  paramsPatientId: PropTypes.string,
  updatePractice: PropTypes.func.isRequired,
  deviceSerialNumber: PropTypes.string,
  patientCreationTogler: PropTypes.func.isRequired,
  setMatchedDevice: PropTypes.func.isRequired,
  practice: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    key: PropTypes.string,
  }),
}

CustomPatientSelector.defaultProps = {
  paramsPatientId: undefined,
  deviceSerialNumber: undefined,
  practice: null,
}

const CreatePatientForm = ({ serialNumber, practiceId }) => {
  const validationSchema = useMemo(() => patientCreationSchema(), [])
  const initialValues = {
    deviceModelId: '',
    deviceSerialNumber: serialNumber,
    practiceId,
  }
  const history = useHistory()

  const redirectToCreation = ({ deviceModelId, deviceSerialNumber }) => {
    history.push(
      toPath.patients
        .new()
        .concat(
          '?',
          queryString.stringify({ deviceModelId, deviceSerialNumber }),
        ),
    )
  }

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={redirectToCreation}
      >
        {({ handleSubmit, values }) => (
          <Form
            onSubmit={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <Divider orientation="left">Device Information</Divider>
            <Row>
              <Col span={19}>
                <DeviceModelSelect
                  {...propsFor('deviceModelId')}
                  labelCol={{ span: 6 }}
                  labelAlign="right"
                  wrapperCol={{ span: 15 }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={19}>
                <Input
                  {...propsFor('deviceSerialNumber')}
                  disabled
                  labelCol={{ span: 6 }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={18}>
                <Form.Item wrapperCol={actionsWrapperCol}>
                  <Button type="primary" htmlType="submit">
                    Create Patient
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  )
}

CreatePatientForm.propTypes = {
  serialNumber: PropTypes.string.isRequired,
  practiceId: PropTypes.string,
}

CreatePatientForm.defaultProps = {
  practiceId: undefined,
}

export default function TransmissionForm({
  deviceSerialNumber,
  practice,
  onSubmit,
}) {
  const validationSchema = useMemo(() => buildValidationSchema(), [])
  const location = useLocation()
  const [selectedPractice, setSelectedPractice] = useState()
  const [showPatientCreationForm, setShowPatientCreationForm] = useState(true)
  const [matchedDevice, setMatchedDevice] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const paramsPatientId = searchParams.get('patientId')

  useEffect(() => {
    if (practice) {
      setSelectedPractice({ key: practice.id, value: practice.name })
    }
  }, [practice])

  return (
    <>
      <Formik
        initialValues={TRANSMISSION_DEFAULTS}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          handleSubmit,
          isSubmitting,
          submitCount,
          setFieldValue,
          values,
        }) => (
          <Form
            onSubmit={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <CustomPatientSelector
              paramsPatientId={paramsPatientId}
              deviceSerialNumber={deviceSerialNumber}
              patientCreationTogler={setShowPatientCreationForm}
              setMatchedDevice={setMatchedDevice}
              practice={selectedPractice}
            />
            {values.patientId && (
              <>
                <Row>
                  <Col span={19}>
                    <Input {...propsFor('firstName')} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col span={19}>
                    <Input {...propsFor('lastName')} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col span={19}>
                    <DatePicker
                      {...propsFor('dateOfBirth')}
                      disabled
                      showToday={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={19}>
                    <Input
                      {...propsFor('practiceMrn')}
                      disabled={!!values.patientId}
                    />
                  </Col>
                </Row>
                <Divider orientation="left">Device Information</Divider>
                <Row>
                  <Col span={19}>
                    <DeviceModelSelect
                      {...propsFor('deviceModelId')}
                      labelCol={{ span: 6 }}
                      labelAlign="right"
                      wrapperCol={{ span: 15 }}
                      disabled={matchedDevice}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={19}>
                    <Input
                      {...propsFor('deviceSerialNumber')}
                      disabled
                      labelCol={{ span: 6 }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={18}>
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      title="Update Transmission"
                      isValid
                      submitCount={submitCount}
                      wrapperCol={actionsWrapperCol}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Form>
        )}
      </Formik>
      {showPatientCreationForm && (
        <CreatePatientForm serialNumber={deviceSerialNumber} />
      )}
    </>
  )
}

TransmissionForm.propTypes = {
  deviceSerialNumber: PropTypes.string,
  practice: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  onSubmit: PropTypes.func.isRequired,
}

TransmissionForm.defaultProps = {
  deviceSerialNumber: undefined,
  practice: null,
}
